/* eslint-disable import/no-cycle */
import _ from 'underscore';
import { PageReady } from '@bingads-webui/page-ready';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { PerfMarkerFactory } from './index';
import { IMarkerTime, IPerfActivity, IPerfMarkerOptions } from './types';

const markerTimeout = 15000; // 15 seconds

let isPageReadyFulfilled = false;

PageReady.afterPageReady().then(() => {
  isPageReadyFulfilled = true;
});

function markTime(): IMarkerTime {
  return {
    time: performance.now(),
    activeTime: _.result(performance, 'activeTime', NaN),
  };
}

function isMarkerValid({ time, activeTime }: IMarkerTime = {} as IMarkerTime): boolean {
  return _.isNumber(time) && time >= 0
    && _.isNumber(activeTime) && activeTime >= 0;
}

function getUrl(): string {
  return (window.location.pathname || '') + (window.location.search || '') + (window.location.hash || '');
}

/**
 * Do not access PerfMarker class directly, use PerfMarker Factory
 */
export class PerfMarker {
  InitUrl: string;

  activeChildren: PerfMarker[];

  activity: IPerfActivity;

  additionalMessage!: Record<string, any>;

  autoEnd: boolean;

  data: Record<string, any>;

  doneCallback: (() => void) | null;

  donePromise: Promise<void>;

  private resolveDonePromise: (() => void) | null;

  hierarchy: string;

  isActiveChild: boolean;

  isCanceled: boolean;

  isDone: boolean;

  isFileFetched: boolean;

  markers: Record<string, IMarkerTime>;

  name: string;

  parentMarker: PerfMarker;

  timeout: number;

  isTimedOut: boolean = false;

  constructor(name: string, markerOptions: IPerfMarkerOptions, isFileFetched: boolean, activity: IPerfActivity, parentMarker: PerfMarker) {
    this.markers = {};
    this.InitUrl = getUrl();
    this.isFileFetched = isFileFetched; // is js file already downloaded or not
    this.name = name;
    this.parentMarker = parentMarker;
    this.hierarchy = this.parentMarker.hierarchy ? `${this.parentMarker.hierarchy}.${this.name}` : this.name;
    this.activity = activity;
    this.isDone = false;
    this.isCanceled = false;
    this.autoEnd = markerOptions.autoEnd;
    this.isActiveChild = markerOptions.isActiveChild;
    this.activeChildren = [];
    this.data = markerOptions.data || {};
    this.timeout = markerOptions.timeout || markerTimeout;
    this.doneCallback = null;
    this.resolveDonePromise = null;
    this.donePromise = new Promise((resolve) => {
      this.resolveDonePromise = resolve;
    });
    setClarityTag({
      PerfMarker: this.name,
      ParentPerfMarker: this.parentMarker.name || 'None',
    });
    setTimeout(() => {
      this.onTimeout();
    }, this.timeout);
  }

  createChild(name: string, markerOptions: IPerfMarkerOptions = { autoEnd: false, isActiveChild: true }) {
    const childMarker = PerfMarkerFactory.createMarker(name, markerOptions, this.activity, this);

    if (childMarker && childMarker.isActiveChild !== false) {
      this.activeChildren.push(childMarker);
    }

    return childMarker;
  }

  // This is useful when different children will be attached to the same parent marker based on some conditions
  // And we can update the parent marker name accordingly for better traceability before creating children
  appendToName(suffix: string) {
    if (!this.name.endsWith(suffix)) {
      this.name = `${this.name}${suffix}`;
      this.hierarchy = `${this.hierarchy}${suffix}`;
    }
  }

  cancel() {
    if (this.isDone || this.isCanceled) {
      return;
    }

    this.isCanceled = true;
    this.markConsole('canceled');

    // Cancel all active children as well
    this.activeChildren.forEach((child) => child.cancel());
  }

  willFetchFile() {
    this.markers.timeFetchFile = markTime();

    this.markConsole('willFetchFile');
  }

  willFetchData() {
    this.markers.timeFetchData = markTime();

    this.markConsole('willFetchData');
  }

  willInit() {
    this.markers.timeInit = markTime();

    this.markConsole('willInit');
  }

  willRender() {
    this.markers.timeRender = markTime();

    this.markConsole('willRender');
  }

  onChildDone(childMarker: PerfMarker) {
    this.activeChildren = _.reject(this.activeChildren, (activeChild) => activeChild === childMarker);

    if (this.autoEnd
      && !this.isDone
      && _.isEmpty(this.activeChildren)) {
      this.done();
    }
  }

  done(additionalMessage = {}) {
    this.markers.timeDone = markTime();
    this.markConsole('done');

    this.additionalMessage = additionalMessage;

    // If marker already done, do not log again
    if (this.isDone || this.isCanceled) {
      return;
    }

    this.send();

    this.isDone = true;

    if (this.doneCallback && _.isFunction(this.doneCallback)) {
      this.doneCallback();
    }

    if (this.resolveDonePromise) {
      this.resolveDonePromise();
    }

    if (this.parentMarker.onChildDone) {
      this.parentMarker.onChildDone(this);
    }
  }

  markConsole(markerPoint: string) {
    if (console.timeStamp) {
      console.timeStamp(`PerfMarker ${this.name} ${markerPoint}`);
    }
  }

  send() {
    const message = this.parseMarkers();

    if (_.isEmpty(message)) {
      return;
    }

    this.activity.perf(JSON.stringify(message), this.hierarchy);
  }

  isUrlChanged() {
    const currentUrl = getUrl();
    return this.InitUrl !== currentUrl;
  }

  parseMarkers() {
    // Calling willFetchFile is optional. If not called, clear file fetching flag
    if (!isMarkerValid(this.markers.timeFetchFile)) {
      this.isFileFetched = true;
    }

    if (_.some([this.markers.timeInit, this.markers.timeDone], (marker) => !isMarkerValid(marker))) {
      return null; // init and done markers should be valid
    }

    const didUrlChange = this.isUrlChanged();

    return {
      Version: 'perf-marker@0.2.0',
      Name: this.name,
      ParentName: this.parentMarker.name,
      Data: this.data,
      IsParentDone: this.parentMarker.isDone,
      IsPageReadyFulfilled: isPageReadyFulfilled,
      IsFileFetched: this.isFileFetched,
      FileFetchStartTime: _.result(this.markers.timeFetchFile, 'time', 0),
      FileFetchActiveStartTime: _.result(this.markers.timeFetchFile, 'activeTime', 0),
      DataFetchStartTime: _.result(this.markers.timeFetchData, 'time', 0),
      DataFetchActiveStartTime: _.result(this.markers.timeFetchData, 'activeTime', 0),
      RenderStartTime: _.result(this.markers.timeRender, 'time', 0),
      RenderActiveStartTime: _.result(this.markers.timeRender, 'activeTime', 0),
      InitStartTime: this.markers.timeInit.time,
      InitActiveStartTime: this.markers.timeInit.activeTime,
      DoneStartTime: this.markers.timeDone.time,
      DoneActiveStartTime: this.markers.timeDone.activeTime,
      DidUrlChange: didUrlChange,
      InitUrl: didUrlChange ? this.InitUrl : '',
      DoneUrl: didUrlChange ? getUrl() : '',
      IsTimedOut: this.isTimedOut,
      AdditionalMessage: this.additionalMessage,
    };
  }

  onTimeout() {
    // if marker is done, canceled, or user moved to a different url, no need to log timeout
    if (this.isDone || this.isCanceled || this.isUrlChanged()) {
      return;
    }
    this.isTimedOut = true;
    const message = `PerfMarker ${this.name} timed out after ${this.timeout}ms without calling done().`;
    this.markConsole(message);

    this.additionalMessage = { IncompleteMarker: message };
    this.markers.timeDone = markTime();

    this.send();
  }
}
